import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Trash2, RefreshCw, AlertCircle, Users, Calendar,
  Image as ImageIcon, MessageCircle, Flag, Bell, Database, Loader2, Check,
  ShieldAlert,
} from 'lucide-react';
import api from '../lib/api';
import { ConfirmDialog } from '../components/Overlay';

/**
 * AdminDemoSeed — pre-launch stress-test community generator.
 * One-click seed / wipe / regenerate of ~100 demo users + full ecosystem.
 *
 * Visible only to admin role. All generated docs are tagged `is_demo: True`.
 */
const SCENARIOS = [
  { key: 'balanced', label: 'Balanced', desc: 'Realistic mix of activity, plans, reports' },
  { key: 'high_activity', label: 'High Activity', desc: '2× likes, vibes, chat messages, joins' },
  { key: 'many_empty_plans', label: 'Many Empty Plans', desc: 'Test empty states & low engagement' },
  { key: 'heavy_reports', label: 'Heavy Reports', desc: 'T&S workflows under load' },
];

const STAT_TILES = [
  { key: 'users', label: 'Users', Icon: Users, accent: '#B6E600' },
  { key: 'plans', label: 'Plans', Icon: Calendar, accent: '#FFC107' },
  { key: 'vibes', label: 'Vibes', Icon: ImageIcon, accent: '#9D7FFF' },
  { key: 'stories', label: 'Stories', Icon: Sparkles, accent: '#FF7BAA' },
  { key: 'group_messages', label: 'Group msgs', Icon: MessageCircle, accent: '#43B5FA' },
  { key: 'dm_messages', label: 'DMs', Icon: MessageCircle, accent: '#FF6B6B' },
  { key: 'reports', label: 'Reports', Icon: Flag, accent: '#FF3B30' },
  { key: 'notifications', label: 'Notifications', Icon: Bell, accent: '#3F5200' },
];

export default function AdminDemoSeed() {
  const navigate = useNavigate();
  const [authChecking, setAuthChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [scenario, setScenario] = useState('balanced');
  const [count, setCount] = useState(100);
  const [busy, setBusy] = useState(null); // 'seed' | 'wipe' | 'regen'
  const [toast, setToast] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [schemaCounters, setSchemaCounters] = useState([]);
  const [schemaAudit, setSchemaAudit] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/admin/me');
        if (alive) setIsAdmin(!!r.data.is_admin);
      } catch { setIsAdmin(false); }
      setAuthChecking(false);
    })();
    return () => { alive = false; };
  }, []);

  const refresh = async () => {
    try {
      const [statsR, countersR] = await Promise.all([
        api.get('/admin/demo/status'),
        api.get('/admin/schema/counters?limit=20'),
      ]);
      setStats(statsR.data);
      setSchemaCounters(countersR.data.rows || []);
    } catch (e) { console.error(e); }
  };

  const runAudit = async () => {
    try {
      const r = await api.get('/admin/schema/audit');
      setSchemaAudit(r.data.results || {});
      setShowAudit(true);
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Audit failed', false);
    }
  };

  const resetCounters = async () => {
    try {
      await api.post('/admin/schema/reset');
      await refresh();
      showToast('Schema counters reset');
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Reset failed', false);
    }
  };

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin]);

  const showToast = useCallback((msg, ok = true) => {
    setToast((prev) => ({ msg, ok, key: (prev?.key || 0) + 1 }));
  }, []);

  const doSeed = async () => {
    setBusy('seed');
    try {
      const r = await api.post('/admin/demo/seed', { scenario, count });
      showToast(`Seed complete — ${r.data.result.users} users created.`);
      await refresh();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Seed failed', false);
    }
    setBusy(null);
  };

  const doWipe = async () => {
    setBusy('wipe');
    try {
      const r = await api.post('/admin/demo/wipe');
      const total = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      showToast(`Wipe complete — ${total} docs removed.`);
      await refresh();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Wipe failed', false);
    }
    setBusy(null);
  };

  const doRegen = async () => {
    setBusy('regen');
    try {
      const r = await api.post('/admin/demo/regenerate', { scenario, count });
      showToast(`Regenerated — ${r.data.result.users} fresh users.`);
      await refresh();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Regenerate failed', false);
    }
    setBusy(null);
    setConfirmAction(null);
  };

  if (authChecking) {
    return (
      <div className="min-h-[100dvh] bg-[#F7F6F2] flex items-center justify-center">
        <RefreshCw size={20} className="text-[#9B9EA3] animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-[100dvh] bg-[#F7F6F2] flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle size={28} className="text-[#FF3B30] mb-3" />
        <h1 className="text-lg font-extrabold text-[#111]">Admin access required</h1>
        <button onClick={() => navigate('/')} className="mt-4 px-4 py-2 bg-[#111] text-white rounded-full text-sm font-bold">Back to home</button>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#F7F6F2]" data-testid="admin-demo-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-black/[0.04]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/admin/analytics')} data-testid="back-btn" aria-label="Back" className="w-9 h-9 rounded-full bg-[#F1F0EA] flex items-center justify-center">
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold tracking-tight text-[#111]">Demo Seed Lab</h1>
            <p className="text-[11px] text-[#6B6E73]">Stress-test the app with a synthetic community</p>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFF3CD] text-[#7A4F00] px-2 py-1 rounded-full">DEMO</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5 space-y-6">
        {/* Warning banner */}
        <div className="rounded-2xl bg-[#FFF3CD] border border-[#F0D27A]/40 p-4 flex items-start gap-3" data-testid="demo-warning">
          <AlertCircle size={18} className="text-[#7A4F00] flex-shrink-0 mt-0.5" />
          <div className="text-xs leading-relaxed">
            <p className="font-extrabold text-[#5C3B00]">Pre-launch sandbox</p>
            <p className="text-[#7A4F00] mt-1">
              Wipe deletes <strong>ALL non-admin users</strong> and all docs tagged{' '}
              <code className="bg-white/60 px-1 rounded">is_demo</code>. Admin login (you) stays intact. Demo users log in with password{' '}
              <code className="bg-white/60 px-1 rounded font-bold">demo123</code>.
            </p>
          </div>
        </div>

        {/* Current state */}
        <section data-testid="demo-stats">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-extrabold text-[#111] text-sm tracking-tight">Current State</h2>
            <button onClick={refresh} className="text-xs text-[#6B8A00] font-bold flex items-center gap-1" data-testid="refresh-stats">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {STAT_TILES.map(t => (
              <div key={t.key} className="bg-white rounded-2xl p-3 border border-black/[0.04]" data-testid={`stat-${t.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <t.Icon size={14} style={{ color: t.accent }} />
                  <span className="text-[9px] uppercase tracking-wider text-[#9B9EA3] font-bold">{t.label}</span>
                </div>
                <p className="text-xl font-extrabold text-[#111] tabular-nums">{stats?.[t.key] ?? '—'}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Scenario picker */}
        <section data-testid="scenario-picker">
          <h2 className="font-extrabold text-[#111] text-sm tracking-tight mb-3">Scenario</h2>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {SCENARIOS.map(s => (
              <button
                key={s.key}
                onClick={() => setScenario(s.key)}
                data-testid={`scenario-${s.key}`}
                className={`text-left p-3 rounded-2xl border-2 transition ${scenario === s.key ? 'border-[#3F5200] bg-[#B6E600]/15' : 'border-black/[0.06] bg-white hover:border-black/[0.12]'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="font-extrabold text-sm text-[#111]">{s.label}</p>
                  {scenario === s.key && <Check size={14} className="text-[#3F5200]" />}
                </div>
                <p className="text-[11px] text-[#6B6E73] leading-snug">{s.desc}</p>
              </button>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <label className="text-xs font-bold text-[#6B6E73]" htmlFor="count-input">User count</label>
            <input
              id="count-input"
              type="number"
              min={5}
              max={500}
              value={count}
              onChange={e => setCount(Math.max(5, Math.min(500, parseInt(e.target.value, 10) || 0)))}
              className="bg-white rounded-xl px-3 py-2 text-sm font-bold w-24 border border-black/[0.05] focus:outline-none focus:border-[#3F5200]"
              data-testid="count-input"
            />
            <span className="text-[11px] text-[#9B9EA3]">5 – 500</span>
          </div>
        </section>

        {/* Action buttons */}
        <section className="space-y-2.5">
          <button
            onClick={doSeed}
            disabled={!!busy}
            data-testid="action-seed"
            className="w-full py-4 rounded-full bg-[#B6E600] text-[#111] font-extrabold text-sm shadow-lg active:scale-[0.98] transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy === 'seed' ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {busy === 'seed' ? 'Seeding…' : `Seed (${count} users)`}
          </button>
          <button
            onClick={() => setConfirmAction('regen')}
            disabled={!!busy}
            data-testid="action-regenerate"
            className="w-full py-4 rounded-full bg-[#111] text-white font-extrabold text-sm active:scale-[0.98] transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy === 'regen' ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {busy === 'regen' ? 'Regenerating…' : 'Wipe + Regenerate'}
          </button>
          <button
            onClick={() => setConfirmAction('wipe')}
            disabled={!!busy}
            data-testid="action-wipe"
            className="w-full py-4 rounded-full bg-white text-[#FF3B30] border-2 border-[#FF3B30]/15 font-extrabold text-sm active:scale-[0.98] transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy === 'wipe' ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            {busy === 'wipe' ? 'Wiping…' : 'Wipe All Demo Data'}
          </button>
        </section>

        {/* Login hint */}
        <section className="bg-white rounded-2xl p-4 border border-black/[0.04]">
          <div className="flex items-center gap-2 mb-2">
            <Database size={14} className="text-[#6B6E73]" />
            <p className="text-xs font-extrabold text-[#111]">How to log in as a demo user</p>
          </div>
          <p className="text-[11px] text-[#6B6E73] leading-relaxed">
            All demo users use the password <code className="bg-[#F1F0EA] px-1 rounded font-bold">demo123</code>.
            Emails follow the pattern <code className="bg-[#F1F0EA] px-1 rounded">first_lastname_NNN@asap.app</code>.
            Browse the user list via <button onClick={() => navigate('/explore')} className="font-bold text-[#6B8A00]">Explore → People</button>.
          </p>
        </section>

        {/* Data Quality — schema mismatch counters + audit */}
        <section data-testid="data-quality" className="bg-white rounded-2xl p-4 border border-black/[0.04]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldAlert size={14} className="text-[#FF3B30]" />
              <p className="text-xs font-extrabold text-[#111]">Data Quality</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={runAudit} data-testid="run-audit-btn" className="text-[10px] font-extrabold uppercase tracking-wider text-[#3F5200] hover:underline">Run audit</button>
              {schemaCounters.length > 0 && (
                <button onClick={resetCounters} data-testid="reset-counters-btn" className="text-[10px] font-extrabold uppercase tracking-wider text-[#FF3B30] hover:underline">Reset</button>
              )}
            </div>
          </div>
          {schemaCounters.length === 0 ? (
            <p className="text-[11px] text-[#6B6E73] italic">No mismatches recorded. Healthy 💚</p>
          ) : (
            <ul className="space-y-1.5" data-testid="schema-counters-list">
              {schemaCounters.slice(0, 8).map((row, i) => (
                <li key={i} className="flex items-center justify-between text-[11px] gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="font-extrabold text-[#111] truncate">{row.collection}.{row.field}</span>
                    <span className="ml-1 text-[#9B9EA3] uppercase tracking-wider text-[9px]">{row.source}</span>
                  </div>
                  <span className="font-extrabold text-[#FF3B30] tabular-nums">{row.count}×</span>
                </li>
              ))}
            </ul>
          )}
          {showAudit && schemaAudit && (
            <div className="mt-3 pt-3 border-t border-black/[0.05]" data-testid="audit-results">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#6B6E73] mb-2">Audit results</p>
              {Object.entries(schemaAudit).map(([coll, info]) => (
                <div key={coll} className="text-[11px] mb-1 flex items-center justify-between">
                  <span className="text-[#111]">
                    <span className="font-bold">{coll}</span> <span className="text-[#9B9EA3]">({info.total})</span>
                  </span>
                  {Object.keys(info.issues || {}).length === 0 ? (
                    <span className="text-[#3F5200] font-bold">✓ clean</span>
                  ) : (
                    <span className="text-[#FF3B30] font-bold">
                      {Object.entries(info.issues).map(([f, n]) => `${f}: ${n}`).join(' · ')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Confirm modal — uses global ConfirmDialog (safe-area, z-above-nav, scrollable, sticky footer) */}
      <ConfirmDialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={confirmAction === 'wipe'
          ? () => { setConfirmAction(null); doWipe(); }
          : doRegen}
        title="Are you sure?"
        description={confirmAction === 'wipe'
          ? 'This will delete every non-admin user, their plans, vibes, stories, chats and reports. Cannot be undone.'
          : `This will wipe everything and create ${count} fresh demo users with scenario "${scenario}".`}
        confirmLabel={confirmAction === 'wipe' ? 'Wipe' : 'Regenerate'}
        cancelLabel="Cancel"
        confirmVariant="danger"
        busy={!!busy}
        testIdPrefix="confirm-modal"
      />

      {/* Toast — click to dismiss. z above BottomNav (9999) and above home indicator. */}
      {toast && (
        <button
          onClick={() => setToast(null)}
          data-testid="demo-toast"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)' }}
          className={`fixed left-1/2 -translate-x-1/2 z-[10090] px-4 py-3 rounded-full font-bold text-xs shadow-2xl ${toast.ok ? 'bg-[#3F5200] text-white' : 'bg-[#FF3B30] text-white'}`}
        >
          {toast.msg} <span className="opacity-60 ml-2">·  tap to dismiss</span>
        </button>
      )}
    </div>
  );
}
